import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import * as React from "react";
import { FC, useState } from "react";
import styled from "styled-components";

export interface LinkItem {
    fields: {
        title: string;
        url: string;
        openInNewWindow: boolean;
    };
}

export const DEFAULT_SECTION_TITLE = "Technical Support Tools";
export const DEFAULT_FOOTNOTE_LINE_1 = "*This content is for members only.";
export const DEFAULT_FOOTNOTE_LINE_2 = "You need to register as a member to use this service.";
export const DEFAULT_MEMBER_BUTTON_TEXT = "Member Registration";
export const DEFAULT_MEMBER_REGISTRATION_URL = "https://store-na.thk.com/SignIn";
export const DEFAULT_MEMBERSHIP_CHECK_TEXT = "Check your membership type (Need Login)";
export const DEFAULT_MEMBERSHIP_CHECK_URL = "https://online.thk.com/mypage/us/en/Account/SimpleUserInfo";

export const DEFAULT_LINKS: LinkItem[] = [
    {
        fields: {
            title: "Designer",
            url: "https://online.thk.com/tools/designer/std/us/en/",
            openInNewWindow: true,
        },
    },
    {
        fields: {
            title: "2D/3D CAD",
            url: "https://online.thk.com/tools/cad/us/en/?languageIso=en",
            openInNewWindow: true,
        },
    },
    {
        fields: {
            title: "Service Life Calculation",
            url: "https://www.thk.com/us/en/products/tools/designer/life_calculator/",
            openInNewWindow: true,
        },
    },
    {
        fields: {
            title: "Product FAQ",
            url: "https://www.thk.com/us/en/products/faq/",
            openInNewWindow: true,
        },
    },
];

export interface TechnicalSupportToolsContentProps {
    variant: "menu" | "drawer";
    sectionTitle?: string;
    links?: LinkItem[];
    footnoteLine1?: string;
    footnoteLine2?: string;
    memberButtonText?: string;
    memberRegistrationUrl?: string;
    membershipCheckText?: string;
    membershipCheckUrl?: string;
}

const TechnicalSupportToolsContent: FC<TechnicalSupportToolsContentProps> = ({
    variant,
    sectionTitle,
    links,
    footnoteLine1,
    footnoteLine2,
    memberButtonText,
    memberRegistrationUrl,
    membershipCheckText,
    membershipCheckUrl,
}) => {
    const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

    const resolvedSectionTitle = sectionTitle ?? DEFAULT_SECTION_TITLE;
    const resolvedLinks = links ?? DEFAULT_LINKS;
    const resolvedFootnoteLine1 = footnoteLine1 ?? DEFAULT_FOOTNOTE_LINE_1;
    const resolvedFootnoteLine2 = footnoteLine2 ?? DEFAULT_FOOTNOTE_LINE_2;
    const resolvedMemberButtonText = memberButtonText ?? DEFAULT_MEMBER_BUTTON_TEXT;
    const resolvedMemberRegistrationUrl = memberRegistrationUrl ?? DEFAULT_MEMBER_REGISTRATION_URL;
    const resolvedMembershipCheckText = membershipCheckText ?? DEFAULT_MEMBERSHIP_CHECK_TEXT;
    const resolvedMembershipCheckUrl = membershipCheckUrl ?? DEFAULT_MEMBERSHIP_CHECK_URL;

    const content = (
        <>
            <StyledWrapper className="gray-box__links">
                {resolvedLinks.map(item => (
                    <Link
                        key={`${item.fields.title}_${item.fields.url}`}
                        href={item.fields.url}
                        target={item.fields.openInNewWindow ? "_blank" : ""}
                        rel={item.fields.openInNewWindow ? "noopener noreferrer" : undefined}
                    >
                        <Typography>
                            {item.fields.title}
                            <Icon src="Copy" />
                        </Typography>
                        <Icon src="ChevronRight" />
                    </Link>
                ))}
            </StyledWrapper>
            {resolvedFootnoteLine1 && <Typography as="p">{resolvedFootnoteLine1}</Typography>}
            {resolvedFootnoteLine2 && <Typography as="p">{resolvedFootnoteLine2}</Typography>}
            {resolvedMemberRegistrationUrl && (
                <Button
                    variant="primary"
                    onClick={() => window.open(resolvedMemberRegistrationUrl, "_blank", "noopener,noreferrer")}
                >
                    <Typography>{resolvedMemberButtonText}</Typography>
                    <ButtonIcon src="Copy" size={12}></ButtonIcon>
                </Button>
            )}
            {resolvedMembershipCheckUrl && (
                <Button
                    variant="primary"
                    onClick={() => window.open(resolvedMembershipCheckUrl, "_blank", "noopener,noreferrer")}
                >
                    <Typography>{resolvedMembershipCheckText}</Typography>
                    <ButtonIcon src="Copy" size={12}></ButtonIcon>
                </Button>
            )}
        </>
    );

    if (variant === "menu") {
        return (
            <StyledWrapper className="gray-box">
                <Typography as="h3">{resolvedSectionTitle}</Typography>
                {content}
            </StyledWrapper>
        );
    }

    return (
        <DrawerContentContainer>
            <AccordionWrapper className="gray-box" $isExpanded={isAccordionExpanded}>
                <Typography as="h3" onClick={() => setIsAccordionExpanded(!isAccordionExpanded)}>
                    <Typography>{resolvedSectionTitle}</Typography>
                    <Icon
                        src={ChevronUp}
                        size={16}
                        style={{
                            transform: isAccordionExpanded ? "rotate(0deg)" : "rotate(180deg)",
                            transition: "transform 0.3s ease",
                        }}
                    />
                </Typography>
                {content}
            </AccordionWrapper>
        </DrawerContentContainer>
    );
};

const DrawerContentContainer = styled.div`
    padding: 20px 22px 24px;

    .gray-box{

        h3{
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: space-between;

            span{

                &[class^="TypographyStyle-sc-"]{
                    flex: 1;
                }

                &[class^="IconWrapper-sc-"]{
                    color: ${({ theme }) => theme.colors.secondary.main};
                    width: 24px;
                    height: 24px;

                    svg{
                        width: 18px;
                        height: 18px;
                    }
                }
            }
        }

        &__links{
            border-bottom: ${({ theme }) => theme.colors.secondary.main} solid 1px;
            margin-bottom: 24px;

            span{
                color: ${({ theme }) => theme.colors.secondary.main};
            }
        }
    }

    a{
        font-size: 14px;
        display: block;
        width: 100%;
        margin-bottom: 14px;

        &:hover{
            opacity: 0.75;

            span{
                color: ${({ theme }) => theme.colors.secondary.main};
            }
        }

        & > span{
            color: ${({ theme }) => theme.colors.secondary.main};
            display: flex;
            justify-content: space-between;
            align-items: center;

            & > span{

                &:first-child{
                    display: flex;
                    align-items: center;
                    gap: 6px;

                    svg{
                        width: 12px;
                        height: 12px;
                    }
                }
            }
        }

        svg{
            color: ${({ theme }) => theme.colors.secondary.main};
            height: 18px;
            width: 18px;
        }
    }

    h3 {
        margin-bottom: 25px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
    }

    p {
        font-size: 12px;
        text-align: center;
        margin-bottom: 0;
    }

    button {
        width: 100%;
        margin-top: 12px;
        color: ${({ theme }) => theme.colors.secondary.contrast};
        border: ${({ theme }) => theme.colors.secondary.contrast} solid 1px;
        gap: 6px;

        span{

            &:last-child{

                margin-left: 6px;

                    svg{
                        color: ${({ theme }) => theme.colors.secondary.contrast};
                    }
            }
        }
    }

    .more-links{

        a{
            width: 100%;
            margin-top: 16px;

            &:hover{

                span{
                    color: ${({ theme }) => theme.colors.primary.main};
                }
            }

            span{
                color: ${({ theme }) => theme.colors.secondary.contrast};
            }

            & > span{
                color: ${({ theme }) => theme.colors.secondary.main};
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
            }
        }
    }
`;

const AccordionWrapper = styled.div<{ $isExpanded: boolean }>`
    color: ${({ theme }) => theme.colors.secondary.main};
    background: ${({ theme }) => theme.colors.secondary.contrast};
    border-radius: 4px;
    padding: 16px 22px 24px;
    height: ${props => (props.$isExpanded ? "auto" : "57px")};
    overflow: hidden;
    transition: height 0.3s ease;

    a{
        font-size: 14px;
        display: block;
        width: 100%;
        margin-bottom: 14px;

        &:hover{
            opacity: 0.75;

            span{
                color: ${({ theme }) => theme.colors.secondary.main};
            }
        }

        & > span{
            color: ${({ theme }) => theme.colors.secondary.main};
            display: flex;
            justify-content: space-between;
            align-items: center;

            & > span{

                &:first-child{
                    display: flex;
                    align-items: center;
                    gap: 6px;

                    svg{
                        width: 12px;
                        height: 12px;
                    }
                }
            }
        }

        svg{
            color: ${({ theme }) => theme.colors.secondary.main};
            height: 18px;
            width: 18px;
        }
    }

    h3 {
        margin-bottom: 25px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
    }

    p {
        font-size: 12px;
        text-align: center;
        margin-bottom: 0;
    }

    button {
        width: 100%;
        margin-top: 12px;
        color: ${({ theme }) => theme.colors.secondary.contrast};
        border: ${({ theme }) => theme.colors.secondary.contrast} solid 1px;
        gap: 6px;

        span{

            &:last-child{

                margin-left: 6px;

                    svg{
                        color: ${({ theme }) => theme.colors.secondary.contrast};
                    }
            }
        }
    }
`;

export default TechnicalSupportToolsContent;
